import React, { useState, useEffect } from "react";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import FarmerTable from "../company/components/FarmerTable";
import AddFarmerModal from "../company/components/AddFarmerModal";
import DownloadFarmersModal from "../company/components/DownloadFarmersModal";
import PendingRequests from "../company/components/PendingRequests";

import MilkCollectionTab from "../company/components/MilkCollectionTab";
import PaymentsTab from "../company/components/PaymentsTab";
import AnalyticsTab from "../company/components/AnalyticsTab";

import { auth, firestore } from "../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import MessagingPanel from "../../components/messaging/MessagingPanel";
import ThreadList from "../../components/messaging/ThreadList";
import ChatModal from "../../components/messaging/ChatModal";

const CompanyFarmer = () => {
  const [activeTab, setActiveTab] = useState("farmers");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [messageThreadId, setMessageThreadId] = useState("");
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  const tabs = [
    { id: "farmers", label: "Farmers List", icon: "🧑‍🌾" },
    { id: "requests", label: "Pending Requests", icon: "📩" },
    { id: "milk", label: "Milk Collection", icon: "🥛" },
    { id: "payments", label: "Payments", icon: "💰" },
    { id: "analytics", label: "Reports & Insights", icon: "📊" },
    { id: "messages", label: "Messages", icon: "💬" },
    { id: "approvals", label: "Approvals", icon: "✅" },
  ];

  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const user = auth.currentUser;

        if (!user) {
          console.warn("User not logged in");
          setLoadingCompany(false);
          return;
        }

        // cached in localStorage?
        const cached = localStorage.getItem(`companyName_${user.uid}`);
        if (cached) {
          setCompanyName(cached);
          setLoadingCompany(false);
          return;
        }

        const userDoc = await getDoc(doc(firestore, "users", user.uid));

        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.companyName) {
            setCompanyName(data.companyName);
            localStorage.setItem(`companyName_${user.uid}`, data.companyName);
          }
        }
      } catch (error) {
        console.error("Error fetching company name:", error);
      } finally {
        setLoadingCompany(false);
      }
    };

    fetchCompanyName();
  }, []);

  return (
    <CompanyLayout>
      <div className="p-6 text-gray-800 transition-all duration-300 overflow-y-auto max-h-[calc(100vh-6rem)]">

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-cyan-700 flex items-center gap-2">
            🧑‍🌾 Farmer Management
          </h1>

          {activeTab === "farmers" && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsDownloadModalOpen(true)}
                disabled={loadingCompany || !companyName}
                className={`${
                  loadingCompany
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-cyan-700 border border-cyan-600 hover:bg-cyan-50"
                } px-4 py-2 rounded-lg shadow-md transition-transform hover:scale-105`}
              >
                Download CSV
              </button>
              <button
                onClick={() => setIsAddModalOpen(true)}
                disabled={loadingCompany || !companyName}
                className={`${
                  loadingCompany
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-cyan-600 hover:bg-cyan-700"
                } text-white px-4 py-2 rounded-lg shadow-md transition-transform hover:scale-105`}
              >
                + Add Farmer
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-4 font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.id
              ? "text-cyan-700 border-b-2 border-cyan-700"
              : "text-gray-500 hover:text-cyan-600"
            }`}
          >
            <span aria-hidden="true">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

        {/* Content */}
        <div className="transition-all duration-500 ease-in-out" key={activeTab}>
          {activeTab === "farmers" && <FarmerTable />}

          {activeTab === "requests" && (
            <PendingRequests companyName={companyName} />
          )}

          {activeTab === "milk" && (
            <MilkCollectionTab companyName={companyName} />
          )}

          {activeTab === "payments" && (
            <PaymentsTab companyName={companyName} />
          )}

          {activeTab === "analytics" && (
            <AnalyticsTab companyName={companyName} />
          )}

          {activeTab === "messages" && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Farmer Messages</h2>
                <p className="text-gray-600 text-sm">
                  Messages from farmers land here. AI monitored for violations.
                </p>
              </div>
              <div className="flex flex-col lg:flex-row gap-4 items-start">
                <ThreadList
                  companyName={companyName}
                  participantType="farmer"
                  selectedParticipantId={messageThreadId}
                  onSelect={(id) => {
                    setMessageThreadId(id);
                    setIsChatModalOpen(true);
                  }}
                />
                <div className="flex-1 w-full">
                  <div className="border border-dashed border-slate-200 rounded-lg p-4 text-sm text-slate-600">
                    Select a conversation from the list to reply. New farmer messages open in a chat modal so you can scroll and respond quickly.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "approvals" && (
            <div className="bg-white rounded-lg shadow p-6 space-y-2">
              <h2 className="text-xl font-semibold text-gray-800">Farmer Approvals</h2>
              <p className="text-gray-600">
                Company Admins and Managers can review and approve any edits or deletions requested for farmer records.
              </p>
              <p className="text-sm text-gray-500">
                Future: integrate specific approval queues for farmer updates and removals.
              </p>
            </div>
          )}
        </div>

        {isAddModalOpen && (
          <AddFarmerModal
            open={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            companyName={companyName}
          />
        )}
        {isDownloadModalOpen && (
          <DownloadFarmersModal
            open={isDownloadModalOpen}
            onClose={() => setIsDownloadModalOpen(false)}
            companyName={companyName}
          />
        )}
        <ChatModal
          open={isChatModalOpen && !!messageThreadId}
          onClose={() => setIsChatModalOpen(false)}
          title={`Chat with ${messageThreadId || "farmer"}`}
          messagingProps={{
            companyName,
            participantId: messageThreadId,
            actorType: "company",
            title: "Company ↔ Farmer chat",
            onConversationDeleted: () => setMessageThreadId(""),
          }}
        />
      </div>
    </CompanyLayout>
  );
};

export default CompanyFarmer;
